import { useCallback } from 'react';

import { useBridgeApi } from '@/api/BridgeApi';
import type { RenderableContentArtifact } from '@/hooks/useContentArtifacts';
import { trackEvent } from '@/utils/fullstory';

type EditChatConfig = {
	message: string;
	trackEventName: string;
	metadata: {
		title: string;
		description: string;
		artifact_type: string;
		icon_url?: string;
	};
};

const deriveEditConfig = (
	artifact: RenderableContentArtifact,
): EditChatConfig => {
	switch (artifact.type) {
		case 'instagram_post': {
			const imageUrls = artifact.content.image_urls ?? [];
			const isCarousel = imageUrls.length > 1;
			const iconUrl = imageUrls[0];
			const title = isCarousel ? 'Instagram Carousel' : 'Instagram Post';
			return {
				message: `Show me this ${title}`,
				trackEventName: 'Edit Mode Opened',
				metadata: {
					title,
					description:
						artifact.content.caption?.substring(0, 100) || 'Edit your post',
					artifact_type: isCarousel ? 'carousel' : artifact.type,
					...(iconUrl && { icon_url: iconUrl }),
				},
			};
		}
		case 'digital_ad': {
			const iconUrl = artifact.content.image_urls?.[0];
			return {
				message: 'Show me this Digital Ad',
				trackEventName: 'Digital Ad Edit Opened',
				metadata: {
					title: 'Digital Ad',
					description:
						artifact.content.headline?.substring(0, 100) || 'Edit your ad',
					artifact_type: 'digital_ad',
					...(iconUrl && { icon_url: iconUrl }),
				},
			};
		}
		case 'email_html':
		case 'email_plaintext': {
			const subject = artifact.content.subject?.trim();
			const body = artifact.content.body?.trim();
			const description = subject
				? subject.substring(0, 100)
				: body
					? body.substring(0, 100)
					: 'Edit your email';
			return {
				message: 'Show me this Email',
				trackEventName: 'Edit Mode Opened',
				metadata: {
					title: 'Email',
					description,
					artifact_type: artifact.type,
				},
			};
		}
	}
};

export const useArtifactEditChat = (artifact: RenderableContentArtifact) => {
	const bridgeApi = useBridgeApi();
	return useCallback(() => {
		const config = deriveEditConfig(artifact);
		trackEvent(config.trackEventName, { artifact_id: artifact.id });
		bridgeApi.sendAction({
			type: 'do_create_chat',
			initial_message: config.message,
			linked_item: {
				type: 'artifact',
				id: artifact.id,
				metadata: config.metadata,
			},
		});
	}, [bridgeApi, artifact]);
};
